import { MapData } from "@/shared/types/types";
import { Route } from '@/shared/types/zod';
import { lineString } from "@turf/helpers";
import length from "@turf/length";
import lineSliceAlong from "@turf/line-slice-along";
import hat from "hat";
import { coordsEqual } from "./MapUtils";


export function addedCoordAt(index: number, legs: Route["properties"]["legs"]) {
	legs.forEach(leg => {
		if (leg.index >= index) {
			leg.index++;
		}
	});
}

//need to run validateLegs manually after this to remove out of bounds legs
export function removedCoordAt(index: number, legs: Route["properties"]["legs"]) {
	for (let i = legs.length - 1; i >= 0; i--) {
		if (legs[i].index > index) {
			legs[i].index--;
		}
	};
}

export function addLeg(route: Route, leg: Route["properties"]["legs"][0]) {
	route.properties.legs.push(leg)
	validateLegs(route)
}

/**
 * Make sure the legs are sorted, removing any that are out of bounds or duplicates. And adding a leg at 0 if needed.
 */
export function validateLegs(route: Route) {
	const legs = route.properties.legs;
	if (!legs || legs.length == 0) return

	legs.sort((a, b) => a.index - b.index);

	// remove duplicates and out of bounds legs
	for (let i = legs.length - 1; i >= 0; i--) {
		if (
			legs[i].index >= route.geometry.coordinates.length - 1 // out of bounds
			|| legs[i].index < 0 // out of bounds
			|| (i < legs.length - 1 && legs[i].index == legs[i + 1].index)) { // duplicate leg at same index
			legs.splice(i, 1);
		}
	}

	// add a leg at 0 if needed
	if (legs.length > 0 && legs[0].index != 0) {
		legs.unshift({
			index: 0,
			id: hat(),
			color: route.properties.color
		});
	}
}

// merge the legs of routeB onto the end of routeA, done before merging the geometries
// run validateLegs manually after the merge! It's too early to do it here.
export function mergeRouteLegs(routeA: Route, routeB: Route) {
	const routeACoordCount = routeA.geometry.coordinates.length;
	for (const leg of routeB.properties.legs) {
		routeA.properties.legs.push({
			...leg,
			index: leg.index + routeACoordCount
		});
	}
}

export function splitRouteLegs(routeA: Route, routeB: Route, index: number) {
	const legs = routeA.properties.legs;
	const leftLegs = legs.filter(leg => leg.index < index);
	const rightLegs = legs.filter(leg => leg.index >= index);

	rightLegs.forEach(leg => {
		leg.index -= index;
	});

	const lastLeftLeg = leftLegs[leftLegs.length - 1];
	if (lastLeftLeg && (rightLegs.length == 0 || rightLegs[0].index != 0)) {
		rightLegs.unshift({
			...lastLeftLeg,
			id: hat(),
			index: 0
		});
	}

	routeA.properties.legs = leftLegs;
	routeB.properties.legs = rightLegs;
	validateLegs(routeA);
	validateLegs(routeB);
}

export function reverseLegs(route: Route) {
	const legs = route.properties.legs;
	const length = route.geometry.coordinates.length;
	if (legs.length > 0) {
		legs.reverse();

		for (let i = legs.length - 1; i >= 1; i--) {
			legs[i].index = Math.max(0, length - legs[i - 1].index - 1);
		}
		legs[0].index = 0;
		validateLegs(route);
	}
}

export function convertColorsToLegs(map: MapData) {
	for (const route of map.routes) {
		if (route.properties.useMultiColor && route.properties.multiColors && route.properties.multiColors?.length > 0 && (!route.properties.legs || route.properties.legs.length == 0)) {
			const colors = route.properties.multiColors.slice();
			colors.sort((a, b) => a.distance - b.distance);
			route.properties.legs = [];
			const segments = route.geometry.coordinates
			for (let i = 0; i < colors.length; i++) {
				let totalLength = 0
				for (let j = 0; j < segments.length - 1; j++) {
					const segment = [...segments[j], segments[j + 1][0]];
					if (segment.length >= 2 && coordsEqual(segment[segment.length - 1], segment[segment.length - 2])) {
						segment.pop();
					}
					const segmentLength = segment.length > 1 ? length(lineString(segment)) : 0;
					totalLength += segmentLength;
					if (totalLength >= colors[i].distance) {
						const breakPoint = colors[i].distance - (totalLength - segmentLength);
						if (breakPoint <= 0) break;
						const first = lineSliceAlong(lineString(segment), 0, breakPoint);
						const second = lineSliceAlong(lineString(segment), breakPoint, segmentLength);
						segments.splice(j, 1, first.geometry!.coordinates, second.geometry!.coordinates);

						// special first leg
						if (route.properties.legs.length == 0) {
							route.properties.legs.push({
								index: 0,
								color: colors[0].distance == 0 ? colors[0].color : route.properties.color,
								id: hat(),
							});
						}

						route.properties.legs.push({
							index: j + 1,
							color: colors[i].color,
							id: hat(),
						})
						break;
					}
				}

			}
		}
		delete route.properties.useMultiColor
		delete route.properties.multiColors
		validateLegs(route)
	}
}